import { engineClass } from "$lib/utils/engineClass";
import { createFetchWithRetry } from "$lib/utils/fetchHelpers";
import { logger } from "$lib/utils/logger";

type AppsignalMetricType = "gauge" | "counter" | "timing" | "histogram";

type EventTags = Record<string, string> & { displayId?: never; engine?: never };

interface AppsignalTelemetryEvent {
  name: string;
  metricType: AppsignalMetricType;
  value: number;
  // One metric name + a tag dimension graphs as a line per tag value; baking
  // the dimension into the name instead is an AppSignal anti-pattern.
  tags?: EventTags;
  omitDisplayId?: boolean;
}

const fetch = createFetchWithRetry({
  logContext: "AppsignalMetrics",
  maxRetriesCount: 3,
  retryOnHttpStatusCodes: [],
});

const METRICS_URL = import.meta.env.VITE_APPSIGNAL_FRONTEND_KEY
  ? `https://appsignal-endpoint.net/metrics/json?api_key=${import.meta.env.VITE_APPSIGNAL_FRONTEND_KEY}`
  : null;

if (!METRICS_URL) {
  // raw console, not logger, mirroring appsignalLogger
  console.warn(
    "[Appsignal] VITE_APPSIGNAL_FRONTEND_KEY not configured; metrics disabled."
  );
}

// Device engine class, tagged on every metric so cache/storage graphs can be
// sliced by hardware class (WPE vs Chromium) and desktop test sessions
// filtered out of fleet aggregates.
const ENGINE = engineClass;

async function uploadToAppsignal(
  displayId: string,
  events: AppsignalTelemetryEvent[]
): Promise<void> {
  if (!METRICS_URL) return;
  const taggedEvents = events.map(({ tags, omitDisplayId, ...event }) => {
    const finalTags: Record<string, string> = {
      ...tags,
      displayId,
      engine: ENGINE,
    };
    if (omitDisplayId) delete finalTags.displayId;
    return { ...event, tags: finalTags };
  });
  try {
    // no-cors, matching the logs pipeline; the response is opaque and the
    // endpoint returns 200 for any payload, so there's nothing to inspect.
    await fetch(METRICS_URL, {
      method: "POST",
      body: JSON.stringify(taggedEvents),
      mode: "no-cors",
    });
  } catch (error) {
    logger.warn(
      "[Appsignal] Failed to upload telemetry events; dropping batch",
      error
    );
  }
}

export { uploadToAppsignal, type AppsignalTelemetryEvent, type EventTags };
